import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  FormDialog,
  Grid,
  InputField,
  MuiBox,
  SelectDropdown,
  Switch,
  Typography
} from '@kis-technologies/kis-ui';
import { ValidPasswordToolTip } from 'components/ValidPasswordToolTip';
import { useActions } from 'hooks/useActions';
import { selectors as adminsSelectors, actions as adminsActions, CreateAdminPayload, Role } from 'store/admins';
import { selectors as authSelectors } from 'store/auth';
import { selectors as venuesSelectors, actions as venuesActions, Venue } from 'store/venues';
import { getSortedList } from 'utils/stringFormat';
import { MAINTENANCE, INVALID, VALID } from '../constants';
import { useStyles } from '../styled';
import { CreateUserOverlayProps, RoleItem, Form } from '../types';
import {
  getInitialCreateForm,
  validationSchema,
  getHasEmptyInput,
  getHasInvalidInput,
  defaultRoles,
  globalRole
} from '../utils';

export const CreateUserOverlay = (props: CreateUserOverlayProps) => {
  const { onCancel, onSuccess } = props;

  // ACTION CREATORS AND SELECTORS
  const [createAdmin, getVenues, getValidPinCode, resetPinCode] = useActions([
    adminsActions.createAdmin,
    venuesActions.getVenues,
    adminsActions.getValidPinCode,
    adminsActions.resetPinCode
  ]);
  const { pending } = useSelector(adminsSelectors.selectAction);
  const venues = useSelector(venuesSelectors.selectVenues);
  const admin = useSelector(authSelectors.selectAdmin);
  const validPinCode = useSelector(adminsSelectors.selectPinCode);
  const { successful } = useSelector(adminsSelectors.selectAction);

  const classes = useStyles();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getInitialCreateForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(venues[0]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);

  // GET venue list if its not available
  useEffect(() => {
    if (!venues || !venues.length) getVenues();
    if (!validPinCode) getValidPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close dialog on successful user creation
  useEffect(() => {
    if (successful) onSuccess();
  }, [successful]);

  useEffect(() => {
    if (venues && venues.length) setSelectedVenue(venues[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  // ON UPDATE OF FORM.ROLE
  useEffect(() => {
    // SET THE FORM TO BE THE PREVIOUS FROM WITH PASSWORD KEY ADDED
    // Resetting password to empty if role change to Maintenance
    if (form.role === MAINTENANCE) {
      setForm((f) => ({ ...f, password: '' }));
    }
  }, [form.role]);

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  useEffect(() => {
    let tempRoles: RoleItem[] = [];
    if (admin && admin.role === 'GlobalAdmin') {
      tempRoles = globalRole;
    }
    tempRoles = tempRoles.concat(defaultRoles);
    setRoles(tempRoles);
  }, [admin]);

  useEffect(() => {
    if (validPinCode) {
      setForm({ ...form, pinCode: validPinCode });
    }

    return () => resetPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPinCode]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // FORM INPUT CHANGE
  const onInputChange = (key: keyof Form, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });

    if (key === 'password') {
      setShowPasswordTooltip(true);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (key: keyof Form, e: React.ChangeEvent<HTMLInputElement>, value: string | null = null) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
    if (key === 'password') {
      setShowPasswordTooltip(false);
    }
  };

  // ON CLICK OF SEND ALERT EMAILS SWITCH
  const onAlertEmailsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SETS FORM MAINTENANCE OR CLIENT ADMIN CHECKBOXES
    setForm({ ...form, sendAlertEmails: !form.sendAlertEmails });
  };

  const handleRoleChange = (role: RoleItem) => {
    setSelectedRole(role);
    setForm({ ...form, role: role.code });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof Form) => {
    return validationMessage[key];
  };

  // FORM SUBMIT
  const onConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SUBMITS THE FORM TO CREATE ADMIN
    const payload: CreateAdminPayload = {
      name: form.name,
      email: form.email,
      password: form.password,
      role: form.role as Role,
      sendAlertEmails: form.sendAlertEmails,
      venueId: form.role === MAINTENANCE && selectedVenue ? selectedVenue.id : undefined,
      pinCode: form.pinCode
    };
    createAdmin(payload);
  };

  // CHECKS ROLE
  const isMaintenance = form.role === MAINTENANCE;

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <FormDialog
      open
      title="Create User"
      cancelButtonAction={onCancel}
      cancelButtonText="Cancel"
      confirmButtonAction={onConfirm}
      confirmButtonDisabled={buttonDisabled}
      confirmButtonIsLoading={pending}
      confirmButtonText="Create"
    >
      <Grid className={classes.formInnerContainer}>
        {/* DROPDOWN - ROLE */}
        <MuiBox className={classes.formDropdownBox}>
          <Typography className={classes.formDropdownLabel}>Role</Typography>
          <SelectDropdown
            placeholder="Select a Role"
            items={getSortedList(roles, 'name')}
            selectedItem={selectedRole as RoleItem}
            onChange={(item: RoleItem) => handleRoleChange(item)}
            itemToString={(item: RoleItem) => (item ? item.name : '')}
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: selectedRole ? '#000' : '#9E9E9E'
              }
            }}
          />
        </MuiBox>
        {/* INPUT - NAME */}
        <MuiBox className={classes.formInputBox}>
          <InputField
            label="Name"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => onInputChange('name', e)}
            onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
            helperText={validate('name')}
            error={validation.name === INVALID}
            sx={{ width: '262px' }}
          />
        </MuiBox>
        {/* INPUT - EMAIL */}
        <MuiBox className={classes.formInputBox}>
          <InputField
            label="Email Address"
            size="small"
            fullWidth
            value={form.email}
            onChange={(e) => onInputChange('email', e)}
            onBlur={(e) => onInputBlur('email', e, e.target.value.trim())}
            helperText={validate('email')}
            error={validation.email === INVALID}
            sx={{ width: { xs: '262px', sm: '524px' } }}
          />
        </MuiBox>
        {/* INPUT - PASSWORD */}
        {!isMaintenance && (
          <MuiBox className={classes.formInputBox}>
            <InputField
              label="Password"
              size="small"
              fullWidth
              value={form.password}
              data-testid="admin-password"
              type="text"
              onChange={(e) => onInputChange('password', e)}
              onBlur={(e) => onInputBlur('password', e, e.target.value.trim())}
              onFocus={() => setShowPasswordTooltip(true)}
              helperText={validate('password')}
              error={validation.password === INVALID}
              endIcon={<ValidPasswordToolTip showValidPasswordInfo={showPasswordTooltip} value={form.password || ''} />}
              sx={{ width: '262px' }}
              inputProps={{ sx: { WebkitTextSecurity: 'disc' } }}
            />
          </MuiBox>
        )}
        <Grid className={classes.formPinBlock}>
          {/* INPUT - PIN CODE */}
          <MuiBox className={classes.formPinInputBox}>
            <InputField
              label="PIN Code"
              size="small"
              value={form.pinCode}
              onChange={(e) => onInputChange('pinCode', e)}
              onBlur={(e) => onInputBlur('pinCode', e, e.target.value.trim())}
              helperText={validate('pinCode')}
              error={validation.pinCode === INVALID}
              sx={{ width: { xs: '262px', sm: '175px' } }}
            />
          </MuiBox>
          {/* BUTTON - GENERATE NEW PIN */}
          <MuiBox className={classes.formButtonBox}>
            <Button
              customSize="small"
              onClick={getValidPinCode}
              isLoading={pending}
              width={200}
              type="submit"
              className={classes.formButton}
            >
              Generate New PIN
            </Button>
          </MuiBox>
        </Grid>
        {/* DROPDOWN - VENUE */}
        {isMaintenance && (
          <MuiBox className={classes.formDropdownBox}>
            <Typography className={classes.formDropdownLabel}>Venue Name</Typography>
            <SelectDropdown
              placeholder="Select a Venue"
              items={getSortedList(venues, 'name')}
              selectedItem={selectedVenue}
              onChange={(item: Venue) => setSelectedVenue(item)}
              itemToString={(item: Venue) => (item ? item.name : '')}
              radius="round"
              width="262px"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedVenue ? '#000' : '#9E9E9E'
                }
              }}
            />
          </MuiBox>
        )}
        {/* SWITCH - SEND EMAILS */}
        <Box flexOverride="none" fullWidth>
          <Card>
            <Card.ActionArea onClick={onAlertEmailsClick}>
              <Grid className={classes.formSwitchBlock}>
                <Switch
                  checked={form.sendAlertEmails}
                  checkedLabel="Send alert emails"
                  uncheckedLabel="Send alert emails"
                  labelSx={{ fontWeight: 600 }}
                />
              </Grid>
            </Card.ActionArea>
          </Card>
        </Box>
      </Grid>
    </FormDialog>
  );
};
